// material-ui
import { useEffect, useRef } from 'react';
import { useTheme, styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
    Avatar,
    Button,
    Box,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemAvatar,
    ListItemSecondaryAction,
    ListItemText,
    Typography
} from '@mui/material';

import { useDispatch } from 'store';
import { activeItem } from 'store/slices/menu';
import { removeNotice, getNotices } from 'store/slices/notices';

// assets
import { IconBrandTelegram, IconMailbox, IconTrash, IconExternalLink } from '@tabler/icons';

// Helper function to check if a string is a valid URL
const isValidUrl = (string) => {
    if (!string) return false;
    try {
        new URL(string);
        return true;
    } catch (_) {
        return false;
    }
};

// styles
const ListItemWrapper = styled('div')(({ theme }) => ({
    cursor: 'pointer',
    padding: 16,
    '&:hover': {
        background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light
    },
    '& .MuiListItem-root': {
        padding: 0
    }
}));

// ==============================|| NOTIFICATION LIST ITEM ||============================== //

const NotificationList = ({ notifications, setOpen }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const displayed = useRef({
        reservations: false
    })

    useEffect(() => {
        console.log('draw');
        Object.keys(displayed.current).forEach(element => {
            displayed.current[element] = false;
        });
    }, [notifications])

    return (
        <List
            sx={{
                width: '100%',
                maxWidth: 700,
                py: 0,
                borderRadius: '10px',
                [theme.breakpoints.down('md')]: {
                    maxWidth: 700
                },
                '& .MuiListItemSecondaryAction-root': {
                    top: 22
                },
                '& .MuiDivider-root': {
                    my: 0
                },
                '& .list-container': {
                    pl: 7
                }
            }}
        >
            {notifications && notifications.length > 0 && notifications.map((notification, index) => {
                if(displayed.current.reservations !== false && moment(notification.date).isBefore(displayed.current.reservations)) {
                    displayed.current.reservations = moment(notification.date);
                }

                // Check if this is a notification with conversation OR with extra URL OR without either
                const hasConversation = !!notification.conversation;
                const hasExtraUrl = isValidUrl(notification.extra);
                const isReservationEnAttente = notification.extra === 'RESERVATION EN ATTENTE';

                // Display notification if it has conversation, extra URL, or neither (but not RESERVATION EN ATTENTE type)
                if(hasConversation || hasExtraUrl || (!isReservationEnAttente && !hasConversation)) {
                    return <Box key={notification.id}>
                        <ListItemWrapper>
                            <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                                <Box>
                                    <Typography variant="subtitle1">{notification.data}</Typography>
                                </Box>
                                <Box>
                                    {hasConversation && (
                                        <Button
                                            onClick={() => {
                                                setOpen(false);
                                                dispatch(activeItem(['chat']));
                                                dispatch(removeNotice(notification.id));
                                                dispatch(getNotices());
                                                navigate(`/app/chat/${notification.conversation}`)
                                            }}
                                            variant="contained"
                                            color="secondary"
                                            disableElevation
                                        >
                                            <IconBrandTelegram stroke={1.5} size="20px" />
                                        </Button>
                                    )}
                                    {!hasConversation && hasExtraUrl && (
                                        <Button
                                            onClick={() => {
                                                setOpen(false);
                                                dispatch(removeNotice(notification.id));
                                                dispatch(getNotices());
                                                window.open(notification.extra, '_blank');
                                            }}
                                            variant="contained"
                                            color="secondary"
                                            disableElevation
                                        >
                                            <IconExternalLink stroke={1.5} size="20px" />
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() => {
                                            dispatch(removeNotice(notification.id));
                                            dispatch(getNotices());
                                        }}
                                        variant="contained"
                                        color="error"
                                        disableElevation
                                        sx={{ ml: 1 }}
                                    >
                                        <IconTrash stroke={1.5} size="20px" />
                                    </Button>
                                </Box>
                            </ListItem>
                        </ListItemWrapper>
                    </Box>
                }

                if(isReservationEnAttente && displayed.current.reservations === false) {
                    displayed.current.reservations = moment(notification.date);
                    return (
                        <Box key={'reservations'}>
                            <ListItemWrapper>
                                <ListItem alignItems="center">
                                    <ListItemAvatar>
                                        <Avatar
                                            sx={{
                                                color: theme.palette.secondary.dark,
                                                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.secondary : theme.palette.secondary.light,
                                                border: theme.palette.mode === 'dark' ? '1px solid' : 'none',
                                                borderColor: theme.palette.primary.main
                                            }}
                                        >
                                            <IconMailbox stroke={1.5} size="20px" />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText primary={<Typography variant="subtitle1">Réservations en attente</Typography>} />
                                    <ListItemSecondaryAction>
                                        <Grid container justifyContent="flex-end">
                                            <Grid item>
                                                <Typography variant="caption" display="block" gutterBottom>
                                                    {displayed.current.reservations.fromNow()}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                <Grid container direction="column" className="list-container">
                                    <Grid item xs={12} sx={{ pb: 2 }}>
                                        <Typography variant="subtitle2">Des réservations sont en attente sur AirBNB. Cliquez ci-dessous pour accéder à la liste.</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid container>
                                            <Grid item xs={12} style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                            }}>
                                                <Button onClick={() => { setOpen(false); dispatch(activeItem(['airbnb-notices'])); navigate('/app/airbnb/notices') }} variant="contained" color="secondary" disableElevation endIcon={<IconBrandTelegram stroke={1.5} size="20px" />}>
                                                    GO
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </ListItemWrapper>
                            <Divider />
                        </Box>
                    )
                } else {
                    return null;
                }
            })}
            {/* <ListItemWrapper>
                <ListItem alignItems="center">
                    <ListItemAvatar>
                        <Avatar alt="John Doe" src={User1} />
                    </ListItemAvatar>
                    <ListItemText primary="John Doe" />
                    <ListItemSecondaryAction>
                        <Grid container justifyContent="flex-end">
                            <Grid item xs={12}>
                                <Typography variant="caption" display="block" gutterBottom>
                                    2 min ago
                                </Typography>
                            </Grid>
                        </Grid>
                    </ListItemSecondaryAction>
                </ListItem>
                <Grid container direction="column" className="list-container">
                    <Grid item xs={12} sx={{ pb: 2 }}>
                        <Typography variant="subtitle2">It is a long established fact that a reader will be distracted</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item>
                                <Chip label="Unread" sx={chipErrorSX} />
                            </Grid>
                            <Grid item>
                                <Chip label="New" sx={chipWarningSX} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </ListItemWrapper>
            <Divider /> */}
            {/* <ListItemWrapper>
                <ListItem alignItems="center">
                    <ListItemAvatar>
                        <Avatar
                            sx={{
                                color: theme.palette.success.dark,
                                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.success.light,
                                border: theme.palette.mode === 'dark' ? '1px solid' : 'none',
                                borderColor: theme.palette.success.main
                            }}
                        >
                            <IconBuildingStore stroke={1.5} size="20px" />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={<Typography variant="subtitle1">Store Verification Done</Typography>} />
                    <ListItemSecondaryAction>
                        <Grid container justifyContent="flex-end">
                            <Grid item xs={12}>
                                <Typography variant="caption" display="block" gutterBottom>
                                    2 min ago
                                </Typography>
                            </Grid>
                        </Grid>
                    </ListItemSecondaryAction>
                </ListItem>
                <Grid container direction="column" className="list-container">
                    <Grid item xs={12} sx={{ pb: 2 }}>
                        <Typography variant="subtitle2">We have successfully received your request.</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item>
                                <Chip label="Unread" sx={chipErrorSX} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </ListItemWrapper>
            <Divider /> */}
            {/* <ListItemWrapper>
                <ListItem alignItems="center">
                    <ListItemAvatar>
                        <Avatar alt="John Doe" src={User1} />
                    </ListItemAvatar>
                    <ListItemText primary={<Typography variant="subtitle1">John Doe</Typography>} />
                    <ListItemSecondaryAction>
                        <Grid container justifyContent="flex-end">
                            <Grid item xs={12}>
                                <Typography variant="caption" display="block" gutterBottom>
                                    2 min ago
                                </Typography>
                            </Grid>
                        </Grid>
                    </ListItemSecondaryAction>
                </ListItem>
                <Grid container direction="column" className="list-container">
                    <Grid item xs={12} sx={{ pb: 2 }}>
                        <Typography component="span" variant="subtitle2">
                            Uploaded two file on &nbsp;
                            <Typography component="span" variant="h6">
                                21 Jan 2020
                            </Typography>
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item xs={12}>
                                <Card
                                    sx={{
                                        backgroundColor:
                                            theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light
                                    }}
                                >
                                    <CardContent>
                                        <Grid container direction="column">
                                            <Grid item xs={12}>
                                                <Stack direction="row" spacing={2}>
                                                    <IconPhoto stroke={1.5} size="20px" />
                                                    <Typography variant="subtitle1">demo.jpg</Typography>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </ListItemWrapper>
            <Divider />
            <ListItemWrapper>
                <ListItem alignItems="center">
                    <ListItemAvatar>
                        <Avatar alt="John Doe" src={User1} />
                    </ListItemAvatar>
                    <ListItemText primary={<Typography variant="subtitle1">John Doe</Typography>} />
                    <ListItemSecondaryAction>
                        <Grid container justifyContent="flex-end">
                            <Grid item xs={12}>
                                <Typography variant="caption" display="block" gutterBottom>
                                    2 min ago
                                </Typography>
                            </Grid>
                        </Grid>
                    </ListItemSecondaryAction>
                </ListItem>
                <Grid container direction="column" className="list-container">
                    <Grid item xs={12} sx={{ pb: 2 }}>
                        <Typography variant="subtitle2">It is a long established fact that a reader will be distracted</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item>
                                <Chip label="Confirmation of Account." sx={chipSuccessSX} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </ListItemWrapper> */}
        </List>
    );
};

export default NotificationList;
